'use client';

import { useCallback, useMemo, useRef, useState } from 'react';

import { BoxProps, useDisclosure } from '@chakra-ui/react';

import { startOfDay } from 'date-fns/startOfDay';

import { dateHelpers, urlHelpers } from '@nocowanie/core/helpers';
import { mapToaster } from '@nocowanie/common-ui/components/molecules/toaster';
import { SearchEngineContainer } from '@nocowanie/common-ui/components/organisms/search-engine-container';
import { useThrottledCallback } from '@nocowanie/common-ui/hooks';
import { SearchEngineModel } from '@nocowanie/common-ui/models';

import { commonUiStateSlice, useAppSelector } from '@app/app-state';
import { environment } from '@app/environments/environment';
import {
    extractGeoIdAndTypeFromAutocompleteItem,
    GeoLocationData,
} from '@app/helpers/autocomplete.helpers';
import { resolveLocationData } from '@app/helpers/search.helpers';
import { useMapAutocompleteData } from '@app/hooks/useMapAutocompleteData';
import { useMapSearch } from '@app/hooks/useMapSearch';
import { AutocompleteItemModel } from '@app/models/elastic-search';

export const MapSearchBar = ({
    isMapLoaded = true,
    ...rest
}: BoxProps & { isMapLoaded?: boolean }) => {
    const mobileDrawerDisclosure = useDisclosure();
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const { searchCriteria, updateSearchCriteria } = useMapSearch();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setFormData] = useState<SearchEngineModel | null>(null);
    const [selectedLocationData, setSelectedLocationData] = useState<GeoLocationData | null>(null);

    // Use ref to store the most recently selected location to handle keyboard navigation
    const lastSelectedLocationRef = useRef<GeoLocationData | null>(null);

    const formApiRef = useRef<{
        setValue: (
            field: keyof SearchEngineModel,
            value: SearchEngineModel[keyof SearchEngineModel],
        ) => void;
        onInputValueUpdated: () => void;
    } | null>(null);

    const initialSearchFormData = useMemo(() => {
        return {
            location: searchCriteria.location,
            geoId: searchCriteria.geoId || '',
            dates: {
                checkInDate: searchCriteria.checkInDate
                    ? startOfDay(new Date(searchCriteria.checkInDate))
                    : dateHelpers.defaultCheckInDate(),
                checkOutDate: searchCriteria.checkOutDate
                    ? startOfDay(new Date(searchCriteria.checkOutDate))
                    : dateHelpers.defaultCheckOutDate(),
            },
            guests: {
                adultsCount: searchCriteria.guests.adultsCount,
                childrenCount: searchCriteria.guests.childrenCount,
                childrenAges: searchCriteria.guests.childrenAges,
            },
            instantReservation: true,
            flexDates: searchCriteria.flexDates ?? environment.DEFAULT_FLEX_DATES,
        };
    }, [searchCriteria]);

    const onSearchEngineDataChanged = useCallback(
        (data: SearchEngineModel) => {
            setFormData(data);
        },
        [setFormData],
    );

    const onFormReady = useCallback(
        (formApi: {
            setValue: (
                field: keyof SearchEngineModel,
                value: SearchEngineModel[keyof SearchEngineModel],
            ) => void;
            onInputValueUpdated: () => void;
        }) => {
            formApiRef.current = formApi;
        },
        [],
    );

    const throttledOnLocationSelected = useThrottledCallback((item: AutocompleteItemModel) => {
        const locationData = extractGeoIdAndTypeFromAutocompleteItem(item);
        setSelectedLocationData(locationData);
        lastSelectedLocationRef.current = locationData;

        if (locationData?.geoId && formApiRef.current) {
            formApiRef.current.setValue('location', item.name);
            formApiRef.current.setValue('geoId', locationData.geoId);
            formApiRef.current.onInputValueUpdated();
        }
    }, 100);

    const autocompleteData = useMapAutocompleteData({
        initialQuery: searchCriteria?.location || '',
        onAccommodationSelected: useCallback(() => {
            mobileDrawerDisclosure.onClose();
        }, [mobileDrawerDisclosure]),
        onLocationSelected: throttledOnLocationSelected,
    });

    const { items, handleItemSelection } = autocompleteData;
    const updateUrlParams = useCallback(
        (data: SearchEngineModel, locationData?: GeoLocationData | null) => {
            const { location, dates, guests, flexDates } = data;

            updateSearchCriteria(
                {
                    location: location,
                    geoId: locationData?.geoId || null,
                    locationType: locationData?.locationType || null,
                    checkInDate: dates?.checkInDate
                        ? dateHelpers.format(dates.checkInDate, 'yyyy-MM-dd')
                        : null,
                    checkOutDate: dates?.checkOutDate
                        ? dateHelpers.format(dates.checkOutDate, 'yyyy-MM-dd')
                        : null,
                    guests: {
                        adultsCount: guests?.adultsCount || 2,
                        childrenCount: guests?.childrenCount || 0,
                        childrenAges: guests?.childrenAges || [],
                        guestCount: (guests?.adultsCount || 2) + (guests?.childrenCount || 0),
                    },
                    flexDates: flexDates ?? environment.DEFAULT_FLEX_DATES,
                },
                true,
            );
        },
        [updateSearchCriteria],
    );

    const onSearchEngineSubmit = useCallback(
        ({ searchEngineData }: { searchEngineData: SearchEngineModel }) => {
            const resolvedLocation = resolveLocationData({
                lastSelectedLocation: lastSelectedLocationRef.current || selectedLocationData,
                searchEngineData,
                items,
                handleItemSelection,
                fallbackLocationType: searchCriteria.locationType,
            });

            if (resolvedLocation.earlyReturn) return;

            const finalData: SearchEngineModel = {
                ...searchEngineData,
                location: resolvedLocation.updatedLocation,
            };

            updateUrlParams(finalData, resolvedLocation.locationData);
            setFormData(null);
            setSelectedLocationData(null);
            lastSelectedLocationRef.current = null;
        },
        [
            updateUrlParams,
            items,
            setFormData,
            selectedLocationData,
            handleItemSelection,
            searchCriteria.locationType,
        ],
    );

    const onDrawerOpen = useCallback(() => {
        mapToaster.dismiss();
    }, []);

    return (
        <SearchEngineContainer
            showSkeleton={!isMapLoaded}
            initialFormData={initialSearchFormData}
            onSearchEngineDataChanged={onSearchEngineDataChanged}
            onSearchEngineSubmit={onSearchEngineSubmit}
            onDrawerOpen={onDrawerOpen}
            onFormReady={onFormReady}
            isV7SearchEngineVersion
            isMobileCompactVersion={false}
            isMobileBrowser={isMobileBrowser}
            autocompleteData={autocompleteData}
            baseUrl={urlHelpers.getMapUrl()}
            mobileDrawerDisclosure={mobileDrawerDisclosure}
            isMapSearch
            hideInstantReservationInput
            {...rest}
        />
    );
};
